import { useState } from 'react';
import { Minus, Plus, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { formatCardioLong, formatCardioSub } from '@/utils/cardioLabel';
import { RoundsHistory } from '@/components/RoundsHistory';

/**
 * Round-count stepper for a round-based cardio block. Changing the rounds
 * re-derives the session length on the server so the weekly tile, the timer and
 * the day total all stay in step.
 */
export function CardioRoundsStepper({ cardioBlock, planId, dayNumber, onUpdated, testId = 'cardio-rounds-stepper' }) {
  const { api } = useAuth();
  const [saving, setSaving] = useState(false);
  const protocol = cardioBlock?.rounds_protocol;
  if (!protocol) return null;

  const sets = protocol.sets || protocol.rounds || 1;

  const change = async (next) => {
    if (next < 3 || next > 15 || saving || !planId || !dayNumber) return;
    setSaving(true);
    try {
      const res = await api.post(`/workouts/${planId}/cardio-rounds`, {
        day_number: dayNumber,
        sets: next,
      });
      if (res.data?.cardio_block) onUpdated?.(res.data.cardio_block);
    } catch {
      /* leave the current value in place on failure */
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 pt-3 border-t border-white/5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-base text-white font-heading font-black uppercase tracking-tight">Rounds</span>
        <div className="flex items-center gap-2 bg-[#1F1F1F] rounded-full p-1 border border-white/10">
          <button
            onClick={() => change(sets - 1)}
            className="w-9 h-9 rounded-full flex items-center justify-center bg-black/50 text-white active:scale-90 active:bg-accent active:text-accent-foreground transition-all"
            data-testid="cardio-rounds-decrease"
            aria-label="One round fewer"
          ><Minus size={16} strokeWidth={2.5} /></button>
          <span className="font-heading text-xl text-white w-10 text-center" data-testid="cardio-rounds-display">
            {saving ? <Loader2 size={16} className="animate-spin mx-auto text-accent" /> : sets}
          </span>
          <button
            onClick={() => change(sets + 1)}
            className="w-9 h-9 rounded-full flex items-center justify-center bg-black/50 text-white active:scale-90 active:bg-accent active:text-accent-foreground transition-all"
            data-testid="cardio-rounds-increase"
            aria-label="One more round"
          ><Plus size={16} strokeWidth={2.5} /></button>
        </div>
        <span className="text-base text-white font-heading font-bold">sets</span>
      </div>
      <p className="mt-2 text-xs text-white/50" data-testid="cardio-rounds-label">
        {formatCardioLong(cardioBlock)}
        <span className="text-white/30"> · {formatCardioSub(cardioBlock)}</span>
        {protocol.rpe_adjusted && (
          <span className="text-accent"> · tuned to your last effort</span>
        )}
      </p>
      <RoundsHistory modalityId={cardioBlock?.modality_id} testId={`${testId}-history`} />
    </div>
  );
}
